import React, { useState, useEffect } from 'react';
import './StudyReminders.css';

export const StudyReminders = ({ onClose }) => {
  const [enabled, setEnabled] = useState(() => {
    return localStorage.getItem('studyRemindersEnabled') === 'true';
  });
  const [reminderTime, setReminderTime] = useState(() => {
    return localStorage.getItem('reminderTime') || '19:00';
  });
  const [permission, setPermission] = useState(Notification.permission);
  const [lastChecked, setLastChecked] = useState(Date.now());

  useEffect(() => {
    // Check every minute if we should send a reminder
    const interval = setInterval(() => {
      checkAndSendReminder();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [enabled, reminderTime]);

  const requestPermission = async () => {
    if ('Notification' in window) {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      return perm === 'granted';
    }
    return false;
  };

  const checkAndSendReminder = () => {
    if (!enabled || permission !== 'granted') return;

    const now = new Date();
    const [hours, minutes] = reminderTime.split(':');
    const reminderDate = new Date();
    reminderDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Check if we're within 1 minute of the reminder time
    const timeDiff = Math.abs(now - reminderDate);
    const oneMinute = 60 * 1000;

    // Check if we haven't sent a reminder today
    const lastReminderDate = localStorage.getItem('lastReminderDate');
    const today = now.toDateString();

    if (timeDiff < oneMinute && lastReminderDate !== today) {
      sendNotification();
      localStorage.setItem('lastReminderDate', today);
    }
  };

  const sendNotification = () => {
    if (permission === 'granted') {
      new Notification('Time to Study! 📚', {
        body: 'Don\'t forget to review your flashcards today!',
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: 'study-reminder',
        requireInteraction: false
      });
    }
  };

  const handleToggle = async () => {
    if (!enabled && permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) {
        alert('Please enable notifications to use study reminders');
        return;
      }
    }

    const newEnabled = !enabled;
    setEnabled(newEnabled);
    localStorage.setItem('studyRemindersEnabled', newEnabled.toString());
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setReminderTime(newTime);
    localStorage.setItem('reminderTime', newTime);
  };

  const testNotification = async () => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) {
        alert('Please enable notifications to test');
        return;
      }
    }
    sendNotification();
  };

  return (
    <div className="study-reminders-overlay" onClick={onClose}>
      <div className="study-reminders-modal" onClick={(e) => e.stopPropagation()}>
        <div className="study-reminders-header">
          <h2>Study Reminders</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="study-reminders-content">
          <div className="reminder-section">
            <div className="reminder-label">
              <span>Daily Reminder</span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="reminder-toggle"
                  checked={enabled}
                  onChange={handleToggle}
                />
                <label htmlFor="reminder-toggle"></label>
              </div>
            </div>
            <p className="reminder-description">
              Get a daily notification to remind you to study
            </p>
          </div>

          {enabled && (
            <div className="reminder-section">
              <label htmlFor="reminder-time" className="reminder-label">
                Reminder Time
              </label>
              <input
                type="time"
                id="reminder-time"
                value={reminderTime}
                onChange={handleTimeChange}
                className="time-input"
              />
            </div>
          )}

          <div className="reminder-section">
            <div className="permission-status">
              <span className="status-label">Notification Permission:</span>
              <span className={`status-badge ${permission}`}>
                {permission === 'granted' && '✓ Granted'}
                {permission === 'denied' && '✗ Denied'}
                {permission === 'default' && '⚠ Not Set'}
              </span>
            </div>
            {permission === 'denied' && (
              <p className="warning-text">
                Please enable notifications in your browser settings
              </p>
            )}
          </div>

          <div className="reminder-actions">
            <button className="test-btn" onClick={testNotification}>
              Test Notification
            </button>
          </div>

          <div className="reminder-info">
            <h3>How it works:</h3>
            <ul>
              <li>Enable the daily reminder toggle</li>
              <li>Set your preferred reminder time</li>
              <li>Keep the app installed as a PWA for best results</li>
              <li>You'll receive a notification at the scheduled time</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
